'use client';

import { useState } from 'react';
import dynamic from 'next/dynamic';
import { Play } from 'lucide-react';
import { TradeCardData } from './trade-types';
import { useLang } from '@/lib/i18n/LanguageContext';

// LiveTradeChart pulls in lightweight-charts — only load it when it's actually the visible chart.
const LiveTradeChart = dynamic(() => import('./LiveTradeChart'), { ssr: false });

interface Props {
  trade: TradeCardData;
  height?: number;
  /** Pass through to LiveTradeChart: timeframe switcher + Fit (detail page only). */
  allowTfSwitch?: boolean;
}

/**
 * Signal chart, TradingView-idea style: the server-drawn static chart image is the star — always
 * visible — with a small secondary Play control that swaps to the live replay (auto-playing forward
 * to the outcome). If a signal has no image yet, the live chart itself is shown so the chart is
 * never a blank screen. Analyses (no levels) just show their uploaded image.
 */
export default function SignalChartPlayer({ trade, height = 480, allowTfSwitch = false }: Props) {
  const { t } = useLang();
  const [playing, setPlaying] = useState(false);
  const img = trade.imageUrl;
  const replayable = trade.ideaType !== 'ANALYSIS';

  // Replay requested, OR no static image on a signal → the live-drawn chart IS the visible chart
  // (never a blank placeholder). autoPlay only when the viewer explicitly hit Play.
  // With the timeframe switcher allowed (detail page), the live chart is always the visible chart —
  // a static image can't change timeframe.
  if ((replayable && (playing || !img)) || (allowTfSwitch && replayable)) {
    // Viewer explicitly hit Play → they're studying the chart, so offer the timeframe
    // switcher there too (not just on the detail page).
    return <LiveTradeChart trade={trade} height={height} autoPlay={playing} allowTfSwitch={allowTfSwitch || playing} />;
  }

  if (!img) {
    return (
      <div className="flex items-center justify-center rounded-xl text-xs" style={{ height, border: '1px solid var(--border-subtle)', background: 'var(--bg-card)', color: 'var(--text-muted)' }}>
        {t('No chart')}
      </div>
    );
  }

  // Static chart image is primary; Play is a small secondary control tucked in the corner.
  return (
    <div className="relative rounded-xl overflow-hidden" style={{ border: '1px solid var(--border-subtle)', background: 'var(--bg-card)' }}>
      <img
        src={img}
        alt={`${trade.ticker} ${trade.direction === 'BUY' ? 'long' : 'short'} chart`}
        className="w-full block"
        style={{ background: 'var(--bg-card)' }}
      />
      {replayable && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setPlaying(true); }}
          aria-label={t('Play replay')}
          className="absolute bottom-3 right-3 inline-flex items-center gap-1.5 rounded-full transition-transform duration-150 hover:scale-105"
          style={{ padding: '7px 12px 7px 10px', background: 'var(--inverse-bg)', color: '#fff', boxShadow: '0 2px 8px rgba(28,43,38,0.35)' }}
        >
          <Play size={13} fill="#fff" />
          <span className="text-xs font-semibold">{t('Replay')}</span>
        </button>
      )}
    </div>
  );
}
